import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal de confirmación "cada 3 ventas" / "al cerrar corte".
 *   - Pide escribir una palabra aleatoria para verificar que no hay
 *     entradas, salidas o transferencias pendientes.
 *   - Si el backend dice `can_skip=true`, muestra botón "Omitir" (1 por día).
 *   - Links separados a Compras, Salidas y Transferencias.
 *   - Al cerrar sin confirmar queda bloqueando el fondo (fixed overlay).
 *
 * Uso:
 *   const { modal, triggerCheck, blockClose } = useSalesConfirmModal();
 *   // ...
 *   await createSale();
 *   await triggerCheck(); // tras venta
 *
 *   // antes de cerrar corte:
 *   const ok = await blockClose();
 *   if (!ok) return;
 */

export function SalesConfirmModal({ status, onConfirm, onSkip, onClose, forceOpen = false }) {
  // status: { must_confirm, can_skip, word, for_close_cut, enabled }
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    if (status?.must_confirm) {
      setTyped('');
      setErr('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [status?.word, status?.must_confirm]);

  if (!status?.must_confirm) return null;

  const expected = (status.word || '').toLowerCase();
  const matches = typed.trim().toLowerCase() === expected && expected.length > 0;

  const doConfirm = async () => {
    if (!matches) { setErr('La palabra no coincide'); return; }
    setBusy(true); setErr('');
    try {
      await onConfirm(typed.trim().toLowerCase());
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo confirmar');
    } finally {
      setBusy(false);
    }
  };

  const doSkip = async () => {
    if (!status.can_skip) return;
    setBusy(true); setErr('');
    try {
      await onSkip();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo omitir');
    } finally {
      setBusy(false);
    }
  };

  const title = status.for_close_cut
    ? '🔒 Confirmación para cerrar corte'
    : '✅ Confirmación de operaciones';

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="sales-confirm-overlay"
      onKeyDown={(e) => { if (e.key === 'Escape') e.preventDefault(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="text-center">
          <div className="text-4xl mb-2">{status.for_close_cut ? '🔒' : '📋'}</div>
          <h2 className="text-lg font-bold text-slate-800">{title}</h2>
          <p className="text-sm text-slate-600 mt-2 leading-snug">
            Antes de continuar, verifica que <b>NO tengas pendientes</b> de registrar:
            <br/>entradas, salidas o transferencias.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => navigate('/purchases')}
            className="bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 rounded-lg py-2 text-xs font-semibold active:scale-95 transition"
            data-testid="sales-confirm-goto-purchases"
          >🛒<br/>Compras</button>
          <button
            onClick={() => navigate('/stock-exits')}
            className="bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-800 rounded-lg py-2 text-xs font-semibold active:scale-95 transition"
            data-testid="sales-confirm-goto-exits"
          >📤<br/>Salidas</button>
          <button
            onClick={() => navigate('/transfers')}
            className="bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-800 rounded-lg py-2 text-xs font-semibold active:scale-95 transition"
            data-testid="sales-confirm-goto-transfers"
          >🔄<br/>Transferencias</button>
        </div>

        <div className="pt-2 border-t border-slate-100">
          <p className="text-xs text-slate-500 mb-1 text-center">
            Si todo está en orden, escribe esta palabra para confirmar:
          </p>
          <div className="text-center text-2xl font-black tracking-widest text-slate-800 bg-slate-50 rounded-lg py-2 select-none" data-testid="sales-confirm-word">
            {status.word}
          </div>
          <input
            ref={inputRef}
            type="text"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={typed}
            onChange={(e) => { setTyped(e.target.value); setErr(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && matches) doConfirm(); }}
            placeholder="Escribe la palabra…"
            className="w-full mt-2 text-center text-lg py-2 border-2 border-slate-200 focus:border-emerald-500 rounded-lg outline-none"
            data-testid="sales-confirm-input"
          />
          {err && <p className="text-xs text-rose-600 text-center mt-1">{err}</p>}
        </div>

        <div className="flex gap-2 pt-1">
          {status.can_skip && !status.for_close_cut && (
            <button
              onClick={doSkip}
              disabled={busy}
              className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 rounded-xl font-medium text-sm disabled:opacity-50"
              data-testid="sales-confirm-skip"
            >Omitir una vez</button>
          )}
          <button
            onClick={doConfirm}
            disabled={busy || !matches}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white py-2.5 rounded-xl font-semibold text-sm"
            data-testid="sales-confirm-submit"
          >{busy ? 'Verificando…' : 'Confirmar'}</button>
        </div>

        {!status.can_skip && !status.for_close_cut && (
          <p className="text-[11px] text-slate-400 text-center">
            Ya omitiste una vez hoy. Esta vez es obligatorio confirmar.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Hook que gestiona el estado y la UI.
 *   - `status` reactivo.
 *   - `triggerCheck()`: consulta estado tras una venta.
 *   - `blockClose()`: pide estado for_close_cut=true y resuelve cuando el user escribe la palabra.
 *   - `modal`: componente ya montado para pintar al final de la página.
 *
 * Hace polling cada 60s para disparar el modal cuando pasan las 2h aunque
 * el cajero esté inactivo.
 */
export function useSalesConfirmModal() {
  const [status, setStatus] = useState(null);
  const [visible, setVisible] = useState(false);
  const resolveRef = useRef(null); // promesa pendiente para blockClose

  const fetchStatus = useCallback(async (forClose = false) => {
    try {
      const { data } = await axios.get(`${API}/sales-confirm/status`, {
        params: { for_close_cut: forClose },
        withCredentials: true,
      });
      return data;
    } catch (e) {
      return { enabled: false, must_confirm: false, for_close_cut: forClose };
    }
  }, []);

  const triggerCheck = useCallback(async () => {
    const s = await fetchStatus(false);
    if (s?.must_confirm) {
      setStatus(s);
      setVisible(true);
    }
  }, [fetchStatus]);

  // Polling automático cada 60s (sólo si no hay modal abierto ya)
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (visible) return;
      const s = await fetchStatus(false);
      if (cancelled) return;
      if (s?.must_confirm) {
        setStatus(s);
        setVisible(true);
      }
    };
    // Primera verificación al montar
    tick();
    const id = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [fetchStatus, visible]);

  const blockClose = useCallback(async () => {
    const s = await fetchStatus(true);
    if (!s?.enabled || !s?.must_confirm) return true;
    setStatus(s);
    setVisible(true);
    return new Promise((resolve) => { resolveRef.current = resolve; });
  }, [fetchStatus]);

  const onConfirm = useCallback(async (word) => {
    const forClose = !!status?.for_close_cut;
    await axios.post(`${API}/sales-confirm/confirm`,
      { word, for_close_cut: forClose },
      { withCredentials: true }
    );
    toast.success(forClose ? '✅ Verificado, cerrando corte…' : '✅ Confirmación OK');
    setVisible(false);
    if (resolveRef.current) { resolveRef.current(true); resolveRef.current = null; }
    setStatus(null);
  }, [status?.for_close_cut]);

  const onSkip = useCallback(async () => {
    await axios.post(`${API}/sales-confirm/skip`, {}, { withCredentials: true });
    toast('Omitido por esta vez. En la siguiente venta será obligatorio.', { icon: '⏭️' });
    setVisible(false);
    setStatus(null);
  }, []);

  const onClose = () => { /* no permitido */ };

  const modal = visible ? (
    <SalesConfirmModal
      status={status}
      onConfirm={onConfirm}
      onSkip={onSkip}
      onClose={onClose}
    />
  ) : null;

  return { modal, triggerCheck, blockClose, status };
}
